import React from 'react';

const team = [
  {
    name: 'Yahya Demeriah',
    photo: '/team-yahya.jpg',
    role: 'CEO & Co-Founder',
    bio: 'Robotics & AI engineer leading product vision and business development. Best Engineer — Professionals at the Rashid bin Hamdan AI & IoT Competition (UAE, 2025), and mentor to 50+ students.',
  },
  {
    name: 'Yamen Al-Kabbani',
    photo: '/team-yamen.jpg',
    role: 'Co-Founder · Mechanical Design',
    bio: 'Robotics & control engineer specializing in SolidWorks mechanical design, 3D printing, and robotics prototyping. First place in Syria — ARC 2026 Sumo Robotics Competition.',
  },
  {
    name: 'Abdulhadi Al-Zhahabi',
    photo: '/team-hadi.jpg',
    role: 'Co-Founder · Electronics & Embedded Systems',
    bio: 'Electronics and embedded systems specialist behind the control hardware, sensors, and power systems that keep our robots running safely in harsh conditions.',
  },
];

const About: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-50 to-secondary-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">About RoboTronics</h1>
            <p className="max-w-3xl mx-auto mt-5 text-xl text-gray-600">
              Smart — efficient solar panel cleaning techniques for a cleaner future.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gray-50 p-8 rounded-lg shadow-sm border-t-4 border-primary">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <p className="text-gray-600">
                Our mission is to revolutionize solar panel maintenance across Syria and the MENA region with water-efficient robotic solutions that maximize energy output, reduce operating costs, and preserve scarce water resources.
              </p>
              <p className="text-gray-600 mt-4">
                We are committed to accelerating renewable energy adoption by removing the maintenance barriers that hold solar installations back — with technology that is affordable, rugged, and built for our region's conditions.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-lg shadow-sm border-t-4 border-secondary">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
              <p className="text-gray-600">
                We envision a region where every solar installation performs at its full potential, maintained by intelligent, water-efficient robots that operate with minimal human intervention and environmental impact.
              </p>
              <p className="text-gray-600 mt-4">
                Starting from Syria and Lebanon, we aim to expand into Jordan, Iraq, the UAE, and Saudi Arabia — becoming the region's leading provider of robotic solar panel maintenance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
          </div>

          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0 md:pr-12">
              <img
                src="/automated-robot.png"
                alt="RoboTronics solar panel cleaning robot"
                className="rounded-lg shadow-md"
              />
            </div>

            <div className="md:w-1/2">
              <p className="text-gray-600 mb-4">
                RoboTronics was founded in Damascus in 2024 by three engineering founders who saw a critical challenge holding back the region's rapid adoption of solar energy: dust accumulation can cut panel output by up to 30%, while traditional cleaning is labor-intensive, expensive, and wasteful of limited water resources.
              </p>
              <p className="text-gray-600 mb-4">
                We set out to build a water-efficient, remote-controlled cleaning robot designed specifically for the climatic conditions of Syria and the wider MENA region — rugged, cost-effective, and simple enough to operate with minimal training.
              </p>
              <p className="text-gray-600 mb-4">
                Our robot was successfully piloted at the Hesia Industrial Zone, effectively cleaning more than 500 panels, and RoboTronics was accepted into the EVOTech incubation program — validating both the technology and its market fit. Since then, we have received growing interest and inquiries from businesses across Syria and Lebanon.
              </p>
              <p className="text-gray-600">
                Today we are developing RoboTronics V2.0 with AI-assisted cleaning, alongside a new Personal Solar Panel Cleaning System for homes, as we prepare to expand into Jordan, Iraq, the UAE, and Saudi Arabia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Founding Team</h2>
            <p className="mt-4 text-xl text-gray-600">
              The engineers behind our robotic solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {team.map((member) => (
              <div key={member.name} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="h-40 bg-gradient-to-br from-primary to-industrial flex items-center justify-center">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-28 h-28 rounded-full object-cover border-2 border-white/40 shadow-md"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-primary font-medium">{member.role}</p>
                  <p className="mt-3 text-gray-600">
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Our Core Values</h2>
            <p className="mt-4 text-xl text-gray-600">
              The principles that guide everything we do at RoboTronics.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 border-l-4 border-primary bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovation</h3>
              <p className="text-gray-600">
                We continuously push the boundaries of what's possible in robotic technology and sustainable solutions.
              </p>
            </div>

            <div className="p-6 border-l-4 border-secondary bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sustainability</h3>
              <p className="text-gray-600">
                We design our products with minimal environmental impact, focusing on water conservation and energy efficiency.
              </p>
            </div>

            <div className="p-6 border-l-4 border-accent bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliability</h3>
              <p className="text-gray-600">
                We build robust solutions that perform consistently in the harshest desert and industrial conditions.
              </p>
            </div>

            <div className="p-6 border-l-4 border-secondary bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Customer Focus</h3>
              <p className="text-gray-600">
                We listen to our customers' needs and continuously improve our products based on their feedback.
              </p>
            </div>

            <div className="p-6 border-l-4 border-accent bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Accessibility</h3>
              <p className="text-gray-600">
                We strive to make advanced robotic solutions affordable and accessible to solar installations of all sizes.
              </p>
            </div>

            <div className="p-6 border-l-4 border-primary bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Integrity</h3>
              <p className="text-gray-600">
                We operate with transparency and honesty in all our business practices and relationships.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
